"use client"
import { memo, useEffect, useState } from "react"
import TrendingModule from "./HomePageSections/TrendingModule"
import CategorySection from "@/components/ui/CategorySection"
import SectionBlock from "@/components/ui/SectionBlock"
import MainLayout from "@/layout/MainLayout"
import MainPageSidebar from "./Sidebars/MainPageSidebar"
import SectionLayout from "@/layout/SectionLayout"
import HeroCard from "@/components/cards/HeroCard"
import axiosInstance from "@/lib/axios"
import StandardCard from "@/components/cards/StandardCard"
import Link from "next/link"
import { HomePageSkeleton } from "@/components/ui/Skeleton"
import { GRID_COUNTS, take } from "@/lib/gridCounts"
import { ListingSectionAd } from "@/components/ListingGridWithAds"

function formatCategoriesWiseData(pageData) {
    const categoriesData = pageData?.categories_wise_data || {}
    return Object.entries(categoriesData).map(([catName, newsItems]) => ({
        cat: catName,
        hero: newsItems[0] || {},
        stories: newsItems.slice(1, 1 + GRID_COUNTS.CATEGORY_HERO_STORIES),
    }))
}

const MainPage = ({ initialData = null }) => {
    const [pageData, setPageData] = useState(() => initialData || null);
    const [categoriesWiseData, setCategoriesWiseData] = useState(() =>
        initialData ? formatCategoriesWiseData(initialData) : []
    )
    const [loading, setLoading] = useState(() => !initialData);
    const [error, setError] = useState(null);

    const applyLandingPayload = (data) => {
        if (!data) return false
        setPageData(data)
        setCategoriesWiseData(formatCategoriesWiseData(data))
        setError(null)
        setLoading(false)
        return true
    }

    const getMainPage = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get(`/landing_page`);
            if (response?.data?.success) {
                applyLandingPayload(response?.data?.data || null)
            }
        } catch (error) {
            console.error("Error fetching landing page:", error);
            setError("मुख्य पृष्ठ लोड करण्यात अडचण आली आहे.")
            return null;
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        if (initialData) return
        getMainPage()
        // eslint-disable-next-line react-hooks/exhaustive-deps -- CSR fallback when no SSR payload
    }, [initialData])

    if (loading) {
        return (
            <MainLayout>
                <HomePageSkeleton />
            </MainLayout>
        );
    }

    if (error || !pageData) {
        return (
            <MainLayout>
                <div className="text-center py-20 min-h-[60vh]">
                    <h2 className="text-2xl font-bold text-gray-800 mb-4">{error || "बातमी सापडली नाही."}</h2>
                    <Link href="/" className="text-(--brand-primary) font-semibold hover:underline">
                        मुख्यपृष्ठावर परत जा
                    </Link>
                </div>
            </MainLayout>
        );
    }

    return (
        <MainLayout>
            <ListingSectionAd page="home" position="horizontal_top" />

            <section>
                <SectionLayout
                    sidebar={
                        <MainPageSidebar data={pageData} />
                    }
                >
                    <HeroCard
                        headline={pageData?.one_latest_news[0]?.title}
                        subtitle={pageData?.one_latest_news[0]?.summary}
                        redirectUrl={`/news/${pageData?.one_latest_news?.[0]?.slug}`}
                        advertisementImage={pageData?.one_latest_news[0]?.featuredImage}
                        data={pageData?.one_latest_news[0]}
                    />

                    <SectionBlock label="ताज्या बातम्या" url="/latest-news" name="Maharashtra">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {take(pageData?.latest_news, GRID_COUNTS.HOMEPAGE_LATEST).map((content) => (
                                <StandardCard
                                    key={content?.id}
                                    headline={content?.title}
                                    layout="col"
                                    imageUrl={content?.featuredImage}
                                    data={content}
                                />
                            ))}
                        </div>
                    </SectionBlock>

                    <div className="lg:hidden">
                        <TrendingModule
                            items={take(pageData?.trending_news, GRID_COUNTS.TRENDING)}
                            isBgColor
                            url="/trending"
                        />
                    </div>

                    <ListingSectionAd page="home" position="horizontal_middle" />

                    {categoriesWiseData?.map((sec, idx) => (
                        <CategorySection
                            key={sec.cat}
                            cat={sec.cat}
                            hero={sec.hero}
                            stories={sec.stories}
                            divider={idx > 0}
                        />
                    ))}
                </SectionLayout>
            </section>
        </MainLayout>
    )
}

export default memo(MainPage)
